import { OTHERWORLD_BACKDROPS } from '../../../game/effects/atmosphere/silhouettes/otherworldBackdrops';
import { stoneBiome } from '../biomeArchetypes';
import { BRANCH_DEPTH_BACKDROPS, BRANCH_DEPTH_HEIGHTS } from '../../../game/effects/atmosphere/silhouettes/branchDepthBackdrops';

/**
 * Moonfall Hollow — an ancient subterranean cavern holding sunken observatory
 * ruins, tide pools, and glowing bioluminescent fungi.
 *
 * Distinct from the crystal-blue cave and cozy forest: deep midnight navy grotto
 * sky, bioluminescent cyan and pale moonlit spores, cold shafts filtering from
 * fractures high above, pulsing fungal-cap glow, ruined observatory columns and
 * spires, and a cool slate-blue vignette.
 */
export const MOONFALL_HOLLOW = stoneBiome({
  id: 'moonfall_hollow',
  sky: { from: 0x050c18, to: 0x14283c, bands: 6 },
  motes: {
    colors: [0x3ee8c0, 0x52f5c0, 0x8ec9ff],
    count: 14,
    sizeMin: 4,
    sizeMax: 10,
    driftPx: 20,
    periodMinMs: 7000,
    periodMaxMs: 15000,
    alphaMin: 0.18,
    alphaMax: 0.55,
  },
  rays: {
    color: 0x5888b0,
    count: 2,
    widthMin: 60,
    widthMax: 130,
    height: 520,
    tiltDeg: -5,
    periodMinMs: 14000,
    periodMaxMs: 22000,
    alphaMin: 0.04,
    alphaMax: 0.12,
  },
  glow: {
    blobs: [
      { color: 0x3ee8c0, atX: 0.28, atY: 0.88, size: 420, alpha: 0.08 },
      { color: 0x4a7a9e, atX: 0.76, atY: 0.72, size: 360, alpha: 0.07 },
    ],
    breathe: { periodMs: 6500, min: 0.70, max: 1.25 },
  },
  lightField: {
    palette: [0x3ee8c0, 0x4a7a9e, 0x22485e],
    count: 8,
    sizeMin: 80,
    sizeMax: 180,
    alphaMin: 0.03,
    alphaMax: 0.10,
    driftRadius: 36,
    periodMinMs: 10000,
    periodMaxMs: 18000,
  },
  flicker: {
    color: 0x4ef0b8,
    size: 180,
    baseAlpha: 0.08,
    flickerAmp: 0.05,
    hzMin: 1.5,
    hzMax: 3.5,
    anchors: [{ atX: 0.22, atY: 0.85 }, { atX: 0.65, atY: 0.78 }],
  },
  extraLayers: [
    {
      kind: 'bioluminescence',
      color: 0x3ee8c0,
      count: 5,
      sizeMin: 70,
      sizeMax: 160,
      alphaMin: 0.06,
      alphaMax: 0.40,
      bandYMin: 0.4,
      bandYMax: 0.9,
      driftPx: 16,
      periodMinMs: 4500,
      periodMaxMs: 8500,
    },
    {
      kind: 'parallax',
      shape: 'stalactites',
      plane: 'ceiling',
      color: 0x07111c,
      speed: 0.03,
      y: 0,
      height: 440,
      alpha: 0.78,
      detail: 1.1,
    },
  ],
  parallax: [
    { svg: BRANCH_DEPTH_BACKDROPS.moonfall, shape: 'spires', color: 0x203749, speed: 0.04, height: BRANCH_DEPTH_HEIGHTS.moonfall, alpha: 0.46, detail: 0.8, sway: { ampPx: 6, periodMs: 38000 } },
    { svg: OTHERWORLD_BACKDROPS.cave.mid, shape: 'columns', color: 0x101c2a, speed: 0.10, height: 580, alpha: 0.82, detail: 1.1 },
    { svg: OTHERWORLD_BACKDROPS.cave.near, shape: 'ridge', color: 0x09121c, speed: 0.18, height: 320, alpha: 0.94, detail: 0.9 },
  ],
  fog: {
    color: 0x2d4858,
    bands: 3,
    alphaMin: 0.04,
    alphaMax: 0.11,
    driftPx: 32,
    periodMinMs: 13000,
    periodMaxMs: 22000,
    yMin: 0.55,
    yMax: 0.92,
    sizeMin: 220,
    sizeMax: 440,
  },
  closer: {
    vignette: { color: 0x02060c, edgeAlpha: 0.42, washColor: 0x10202e, washAlpha: 0.05 },
    grade: { top: 0x3a5268, bottom: 0x0c1824, strength: 0.15, blend: 'normal' },
  },
});
